import { z } from 'zod';

import { defineService } from '@@api/lib/defineService';
import { BreakingNews } from '@@api/services/breakingnews/schemas';
import {
    NewTickerEvent,
    TickerEvent,
    TickerEventBase,
    UnityTickerEvent,
    UnityTickerEventReorder,
} from '@@api/services/liveticker/schemas/event';
import { League, NewTeam, Team, UnityTeam } from '@@api/services/liveticker/schemas/team';
import {
    NewTicker,
    Ticker,
    TickerCommon,
    TickerTypesTypes,
    UnityTicker,
} from '@@api/services/liveticker/schemas/ticker';
import {
    deserializeLeague,
    deserializeTeam,
    deserializeTicker,
    deserializeTickerEvent,
    serializeNewTicker,
    serializeTeam,
    serializeTicker,
    serializeTickerEvent,
} from '@@api/services/liveticker/transformers';
import { PaginationParams, SearchParams } from '@@api/utils/schemas/queryParams';
import { Ids } from '@@api/utils/schemas/schemas';
import { paginated } from '@@api/utils/schemas/utils';
import config from '@@config';

export const tickersService = defineService(
    (defineEndpoint) => ({
        ticker: {
            post: defineEndpoint({
                method: 'POST',
                path: '/tickers',
                body: NewTicker.transform(serializeNewTicker),
                responses: {
                    201: UnityTicker.transform(deserializeTicker),
                },
            }),
            replace: defineEndpoint({
                method: 'POST',
                path: '/tickers',
                body: z.null(),
                query: z.object({
                    breakingnewsId: BreakingNews.shape.id,
                }),
                responses: {
                    201: UnityTicker.transform(deserializeTicker),
                },
            }),
            fromArticle: defineEndpoint({
                method: 'POST',
                path: '/tickers',
                body: z.null(),
                query: z.object({
                    articleId: z.string(),
                }),
                responses: {
                    201: UnityTicker.transform(deserializeTicker),
                },
            }),
            delete: defineEndpoint({
                method: 'DELETE',
                path: '/tickers/:id',
                pathParams: z.object({
                    id: TickerCommon.shape.id,
                }),
                responses: {
                    204: z.never(),
                },
            }),
            get: defineEndpoint({
                method: 'GET',
                path: '/tickers/:id',
                omitContentLanguageFromKey: true,
                pathParams: z.object({
                    id: TickerCommon.shape.id,
                }),
                responses: {
                    200: UnityTicker.transform(deserializeTicker),
                },
            }),
            put: defineEndpoint({
                method: 'PUT',
                path: '/tickers/:id',
                pathParams: z.object({
                    id: TickerCommon.shape.id,
                }),
                body: Ticker.transform(serializeTicker),
                responses: {
                    200: UnityTicker.transform(deserializeTicker),
                },
            }),
        },
        leagues: {
            getAll: defineEndpoint({
                method: 'GET',
                path: '/leagues',
                query: z.object({
                    ...SearchParams.pick({ q: true }).shape,
                    ...PaginationParams.shape,
                    sportTypes: z.enum(['soccer,iceHockey', 'tennis', 'soccer', 'iceHockey']),
                }),
                responses: {
                    200: paginated(League.transform(deserializeLeague)),
                },
            }),
            get: defineEndpoint({
                method: 'GET',
                path: '/leagues/:id',
                omitContentLanguageFromKey: true,
                pathParams: z.object({
                    id: z.number(),
                }),
                responses: {
                    200: League.transform(deserializeLeague),
                },
            }),
            post: defineEndpoint({
                method: 'POST',
                path: '/leagues',
                body: League,
                responses: {
                    201: League.transform(deserializeLeague),
                },
            }),
            put: defineEndpoint({
                method: 'PUT',
                path: '/leagues/:id',
                pathParams: z.object({
                    id: z.number(),
                }),
                body: League,
                responses: {
                    200: League.transform(deserializeLeague),
                },
            }),
            delete: defineEndpoint({
                method: 'DELETE',
                path: '/leagues/:id',
                pathParams: z.object({
                    id: z.number(),
                }),
                responses: {
                    204: z.never(),
                },
            }),
        },
        teams: {
            getAll: defineEndpoint({
                method: 'GET',
                path: '/teams',
                query: z.object({
                    ...SearchParams.pick({ q: true }).shape,
                    ...PaginationParams.shape,
                    leagueIds: Ids.optional(),
                    sportType: z.enum(['soccer', 'iceHockey', 'tennis']),
                }),
                responses: {
                    200: paginated(UnityTeam.transform(deserializeTeam)),
                },
            }),
            get: defineEndpoint({
                method: 'GET',
                path: '/teams/:id',
                omitContentLanguageFromKey: true,
                pathParams: z.object({
                    id: z.number(),
                }),
                responses: {
                    200: UnityTeam.transform(deserializeTeam),
                },
            }),
            post: defineEndpoint({
                method: 'POST',
                path: '/teams',
                body: NewTeam.transform(serializeTeam),
                responses: {
                    200: UnityTeam.transform(deserializeTeam),
                    201: UnityTeam.transform(deserializeTeam),
                },
            }),
            put: defineEndpoint({
                method: 'PUT',
                path: '/teams/:id',
                pathParams: z.object({
                    id: z.number(),
                }),
                body: Team.transform(serializeTeam),
                responses: {
                    200: UnityTeam.transform(deserializeTeam),
                },
            }),
            delete: defineEndpoint({
                method: 'DELETE',
                path: '/teams/:id',
                pathParams: z.object({
                    id: z.number(),
                }),
                responses: {
                    204: z.never(),
                },
            }),
        },
        events: {
            getAll: defineEndpoint({
                method: 'GET',
                path: '/tickers/:id/events',
                pathParams: z.object({
                    id: TickerCommon.shape.id,
                }),
                query: z.object({
                    ...PaginationParams.shape,
                    keyEvent: z.boolean().optional(),
                }),
                responses: {
                    200: paginated(UnityTickerEvent.transform(deserializeTickerEvent)),
                },
            }),
            get: defineEndpoint({
                method: 'GET',
                path: '/tickers/:tickerId/events/:eventId',
                pathParams: z.object({
                    tickerId: TickerCommon.shape.id,
                    eventId: TickerEventBase.shape.id,
                }),
                responses: {
                    200: UnityTickerEvent.transform(deserializeTickerEvent),
                },
            }),
            post: defineEndpoint({
                method: 'POST',
                path: '/tickers/:id/events',
                pathParams: z.object({
                    id: TickerCommon.shape.id,
                }),
                query: z.object({
                    after: z.string().nullish(),
                }),
                body: NewTickerEvent.transform(serializeTickerEvent),
                responses: {
                    200: UnityTickerEvent.transform(deserializeTickerEvent),
                    201: UnityTickerEvent.transform(deserializeTickerEvent),
                },
            }),
            put: defineEndpoint({
                method: 'PUT',
                path: '/tickers/:tickerId/events/:eventId',
                pathParams: z.object({
                    tickerId: TickerCommon.shape.id,
                    eventId: TickerEventBase.shape.id,
                }),
                body: TickerEvent.transform(serializeTickerEvent),
                responses: {
                    200: UnityTickerEvent.transform(deserializeTickerEvent),
                },
            }),
            patch: defineEndpoint({
                method: 'PATCH',
                path: '/tickers/:tickerId/events',
                pathParams: z.object({
                    tickerId: TickerCommon.shape.id,
                }),
                body: UnityTickerEventReorder,
                responses: {
                    200: z.never(),
                },
            }),
            delete: defineEndpoint({
                method: 'DELETE',
                path: '/tickers/:tickerId/events/:eventId',
                pathParams: z.object({
                    tickerId: TickerCommon.shape.id,
                    eventId: TickerEventBase.shape.id,
                }),
                responses: {
                    204: z.never(),
                },
            }),
        },
        types: {
            get: defineEndpoint({
                method: 'GET',
                path: '/types',
                responses: {
                    200: TickerTypesTypes,
                },
            }),
        },
    }),
    {
        baseUrl: config.apiEnvVars.livetickerUrl,
    },
);
